import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Star, Newspaper, Radio, Square, Loader2, RefreshCw, Search, Bell, Bookmark, Rss, X, Flame, Plus, Zap, CalendarRange, Volume2, Share2, MailPlus, Coffee, Sunrise, Footprints, Sparkles, ListMusic } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAccent } from "@/context/AccentContext";
import { LanguageSelect } from "@/components/LanguageSelect";
import { READ_VOICES, VoicePreview } from "@/components/VoiceControls";
import { useSpeaker } from "@/components/home/useSpeaker";
import { StoryCard } from "@/components/news/StoryCard";
import { ListenStreak } from "@/components/news/ListenStreak";
import { SavedStory } from "@/components/news/SavedStory";
import { WeekendList } from "@/components/news/WeekendList";
import { WalkHistory } from "@/components/news/WalkHistory";
import { FolderComments } from "@/components/news/FolderComments";
import { FolderCommentAlerts } from "@/components/news/FolderCommentAlerts";
import { PinSuggestions } from "@/components/news/PinSuggestions";
import { ListenLine } from "@/components/news/ListenLine";
import { ListenQueue } from "@/components/news/ListenQueue";
import { BoxPlayers } from "@/components/news/BoxPlayers";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ReadAlong } from "@/components/news/ReadAlong";
import { ListenHistory } from "@/components/news/ListenHistory";
import { MorningChoice } from "@/components/news/MorningChoice";
import { DigestPreview } from "@/components/news/DigestPreview";
import { BlockedPublishers } from "@/components/news/BlockedPublishers";
import { CountryPicker } from "@/components/news/CountryPicker";
import { PageGuide } from "@/components/PageGuide";
import { CompareCoverage } from "@/components/news/CompareCoverage";
import { Flag } from "@/components/Flag";
import { ResumeListen } from "@/components/news/ResumeListen";
import { recall, remember } from "@/lib/newsCache";
import { bulletin, wholeStory } from "@/lib/storyRead";

const TABS = [
  { key: "feed", label: "My feed", icon: Rss },
  { key: "trending", label: "Trending", icon: Flame },
  { key: "search", label: "Search", icon: Search },
  { key: "weekend", label: "Weekend", icon: Sunrise },
  { key: "saved", label: "Saved", icon: Bookmark },
];
const RANGES = [{ v: 1, label: "24 hours" }, { v: 7, label: "A week" }, { v: 30, label: "A month" }];

export default function News() {
  const { languages, lang } = useI18n();
  const { accent } = useAccent();
  const [tab, setTab] = useState("feed");
  const [alertsShown, setAlertsShown] = useState(5);
  const [digestShown, setDigestShown] = useState(5);
  const [dailyPreview, setDailyPreview] = useState(false);
  const [nextUp, setNextUp] = useState(null);
  const [feed, setFeed] = useState(null);
  const [cats, setCats] = useState({ mine: [], all: [] });
  const [digest, setDigest] = useState(null);
  const [saved, setSaved] = useState([]);
  const [savedLang, setSavedLang] = useState(() => localStorage.getItem("chatmilu.news.savedlang") || "");
  const [savedBusy, setSavedBusy] = useState(false);
  const [folders, setFolders] = useState({ folders: [], counts: {}, total: 0 });
  const [folder, setFolder] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const [noteQuery, setNoteQuery] = useState("");
  const [shares, setShares] = useState({});
  const [sunday, setSunday] = useState({ folder: "", on: false });
  const [walk, setWalk] = useState(null);
  const [pages, setPages] = useState({});
  const [walksKey, setWalksKey] = useState(0);
  const [tiles, setTiles] = useState({});
  const [waiting, setWaiting] = useState(0);
  const [walkLength, setWalkLength] = useState(
    () => Number(localStorage.getItem("chatmilu.news.walklength")) || 10);
  const [quickOnly, setQuickOnly] = useState(() => localStorage.getItem("chatmilu.news.quick") === "1");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [voice, setVoice] = useState(() => localStorage.getItem("chatmilu.brief.voice") || "nova");
  const [speed, setSpeed] = useState(() => Number(localStorage.getItem("chatmilu.brief.speed")) || 1);
  const [readLang, setReadLang] = useState(() => localStorage.getItem("chatmilu.brief.lang") || "");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("");
  const [days, setDays] = useState(7);
  const [settings, setSettings] = useState({ digest_hour: 7, digest_size: 5, muted_sources: [], timezone: "" });
  const [pinned, setPinned] = useState([]);
  const [lineUp, setLineUp] = useState([]);
  const [spot, setSpot] = useState(null);
  const [bulletinWords, setBulletinWords] = useState({});
  const [bothLangs, setBothLangs] = useState(
    () => localStorage.getItem("chatmilu.news.bothlangs") === "1");
  const [alsoTitles, setAlsoTitles] = useState({});
  const [compare, setCompare] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [only, setOnly] = useState("");
  const [results, setResults] = useState(null);
  const [trending, setTrending] = useState(null);
  const [word, setWord] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [nearOnly, setNearOnly] = useState(false);
  const [roundup, setRoundup] = useState(null);
  const [streak, setStreak] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [searching, setSearching] = useState(false);
  const [heard, setHeard] = useState(0);  // bumped when a listen ends, so history refreshes
  const speaker = useSpeaker(voice, speed);
  const said_in = readLang || lang;
  const sundayEvening = new Date().getDay() === 0 && new Date().getHours() >= 17;

  const loadSaved = useCallback(async (code, box, words) => {
    const want = code === undefined ? savedLang : code;
    const where = box === undefined ? folder : box;
    const q = words === undefined ? noteQuery : words;
    setSavedBusy(true);
    try {
      const params = {};
      if (want) params.lang = want;
      if (where) params.folder = where;
      if (q.trim()) params.q = q.trim();
      const [list, boxes, shares, sunday] = await Promise.all([
        api.get("/news/saved", { params }),
        api.get("/news/folders"),
        api.get("/news/shares"),
        api.get("/news/folders/digest"),
      ]);
      setSaved(list.data.saved);
      setFolders(boxes.data);
      setShares(shares.data.shares || {});
      setSunday(sunday.data);
    } catch (e) {
      console.error("could not load saved stories", e);
    } finally {
      setSavedBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedLang, folder, noteQuery]);

  // a coffee break: anything measured at five minutes or less
  const coffeeBreak = (rows) =>
    quickOnly ? (rows || []).filter((r) => r.read_min && r.read_min <= 5) : rows || [];

  // three at a time per category; Refresh rolls on to the next three
  const nextThree = (key) => setPages((p) => ({ ...p, [key]: (p[key] || 0) + 1 }));

  const playWalk = async () => {
    await speaker.play("walk", async () => {
      const { data } = await api.get("/news/walk",
                                    { params: { lang: said_in, minutes_wanted: walkLength } });
      setWalk(data);
      setWalksKey((n) => n + 1);
      return data.text;
    });
  };

  // one tap moves every category on to its next three
  const refreshAll = () => {
    setPages((p) => Object.fromEntries(
      (feed?.sections || []).map((sec) => [sec.key, (p[sec.key] || 0) + 1])));
    toast.success("Three fresh stories in every category");
  };

  const tileKey = (source) => String(source || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 60);

  const pickTileSkin = async (source, skin) => {
    setTiles((t) => ({ ...t, [tileKey(source)]: skin }));
    try {
      await api.put("/news/tiles", { source, skin });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that colour");
    }
  };

  const pickWalkLength = (mins) => {
    setWalkLength(mins);
    localStorage.setItem("chatmilu.news.walklength", String(mins));
  };

  const toggleQuick = () => {
    const on = !quickOnly;
    setQuickOnly(on);
    localStorage.setItem("chatmilu.news.quick", on ? "1" : "0");
  };

  const searchNotes = (words) => {
    setNoteQuery(words);
    loadSaved(undefined, undefined, words);
  };

  const toggleSunday = async (name) => {
    const on = !(sunday.on && sunday.folder === name);
    try {
      const { data } = await api.put("/news/folders/digest", { folder: name, on });
      setSunday(data);
      toast.success(on ? `MiLu will email you “${name}” on Sunday mornings`
                       : "Sunday folder email switched off");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not change that");
    }
  };

  const previewSunday = async (name) => {
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/news/folders/digest/preview", { folder: name });
      setPreview(data);
      toast[data.sent ? "success" : "error"](
        data.sent ? `Sent to ${data.to} — it's in your inbox`
                  : data.reason || "MiLu could not post it, but here's how it looks",
        { duration: data.sent ? 5000 : 9000 });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build that preview");
    } finally {
      setPreviewBusy(false);
    }
  };

  const shareFolder = async (name) => {
    try {
      const { data } = await api.post(`/news/folders/${encodeURIComponent(name)}/share`);
      const link = `${window.location.origin}/news/shared/${data.token}`;
      setShares((prev) => ({ ...prev, [name]: data.token }));
      navigator.clipboard?.writeText(link).catch(() => {});
      toast.success("Link copied — anyone can read this folder", { duration: 6000 });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not share that folder");
    }
  };

  const unshareFolder = async (name) => {
    try {
      await api.delete(`/news/folders/${encodeURIComponent(name)}/share`);
      setShares((prev) => { const next = { ...prev }; delete next[name]; return next; });
      toast.success(`“${name}” is private again`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not stop sharing");
    }
  };

  const pickSavedLang = (code) => {
    setSavedLang(code);
    localStorage.setItem("chatmilu.news.savedlang", code);
    loadSaved(code);
  };

  const pickFolder = (box) => {
    setFolder(box);
    loadSaved(undefined, box);
  };

  const addFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    try {
      await api.post("/news/folders", { name });
      setNewFolder("");
      toast.success(`Folder “${name}” added`);
      loadSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not add that folder");
    }
  };

  const dropFolder = async (name) => {
    try {
      await api.delete(`/news/folders/${encodeURIComponent(name)}`);
      toast.success(`“${name}” removed — its stories are now unsorted`);
      if (folder === name) setFolder("");
      loadSaved(undefined, folder === name ? "" : folder);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that folder");
    }
  };

  const playSavedDigest = async () => {
    await speaker.play("saved-digest", async () =>
      (await api.get("/news/saved/say", {
        params: { lang: savedLang || said_in, ...(folder && folder !== "unsorted" ? { folder } : {}) },
      })).data.text);
  };

  const loadFeed = async (fresh = true) => {
    const key = `feed:${said_in}`;
    if (!fresh) {
      const kept = recall(key);
      if (kept) {
        setFeed(kept);
        setCats((c) => ({ ...c, mine: kept.mine }));
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    try {
      const { data } = await api.get("/news/feed", { params: { lang: said_in } });
      setFeed(data);
      remember(key, data);
      setCats((c) => ({ ...c, mine: data.mine }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load your feed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed(false);  // coming back to the tab should not cost a reload
    api.get("/news/tiles").then(({ data }) => setTiles(data.tiles || {})).catch(() => {});
    api.get("/news/categories").then(({ data }) => setCats(data))
      .catch((e) => console.error("could not load categories", e));
    api.get("/news/digest", { params: { lang: said_in } }).then(({ data }) => setDigest(data))
      .catch((e) => console.error("could not load the digest", e));
    loadSaved();
    api.get("/news/settings").then(({ data }) => {
      setSettings(data);
      setPinned(data.pinned_sources || []);
      if (data.voice) setVoice(data.voice);
      if (data.speed) setSpeed(Number(data.speed));
    }).catch((e) => console.error("could not load news settings", e));
    api.get("/news/trending").then(({ data }) => setTrending(data.rows))
      .catch((e) => console.error("could not load trending stories", e));
    api.get("/news/alerts").then(({ data }) => setAlerts(data.alerts))
      .catch((e) => console.error("could not load alerts", e));
    api.get("/news/roundup").then(({ data }) => setRoundup(data))
      .catch((e) => console.error("could not load the roundup", e));
    api.get("/news/streak").then(({ data }) => setStreak(data))
      .catch((e) => console.error("could not load the reading streak", e));
    api.get("/news/listen/spot").then(({ data }) => {
      if (data.stories?.length) {
        setSpot(data);
        setLineUp(data.stories);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // remember which story is being read, so an interrupted listen can be picked up
  useEffect(() => {
    if (!speaker.playing || lineUp.length === 0) return;
    const at = lineUp.findIndex((s) => `${s.story_id}-read` === speaker.playing);
    if (at >= 0) keepSpot(lineUp, at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speaker.playing, lineUp]);

  // each story MiLu opens belongs in the history below
  useEffect(() => {
    setHeard((n) => n + 1);
  }, [speaker.playing]);

  // a quiet count of what has landed since this feed was loaded — nothing reloads on its own
  useEffect(() => {
    if (tab !== "feed" || !feed?.sections) return;
    const seen = feed.sections.flatMap((sec) => sec.rows.map((r) => r.story_id)).join(",");
    if (!seen) return;
    const look = () => api.get("/news/fresh", { params: { seen, lang: said_in } })
      .then(({ data }) => setWaiting(data.fresh))
      .catch(() => {});
    look();
    const timer = setInterval(look, 180000);
    return () => clearInterval(timer);
  }, [tab, feed, said_in]);

  // saved stories change while browsing the feed, so refresh them whenever the tab is opened
  useEffect(() => {
    if (tab === "saved") loadSaved();
  }, [tab, loadSaved]);

  const pickLanguage = async (code) => {
    setReadLang(code);
    localStorage.setItem("chatmilu.brief.lang", code);
    setLoading(true);
    try {
      const { data } = await api.get("/news/feed", { params: { lang: code } });
      setFeed(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not reload in that language");
    } finally {
      setLoading(false);
    }
  };

  const pickVoice = (id) => {
    setVoice(id);
    localStorage.setItem("chatmilu.brief.voice", id);
    saveVoice({ voice: id });
  };

  const pickSpeed = (rate) => {
    setSpeed(rate);
    localStorage.setItem("chatmilu.brief.speed", String(rate));
    saveVoice({ speed: rate });
  };

  // how MiLu sounds follows the reader, not the browser
  const saveVoice = async (patch) => {
    try {
      await api.put("/news/settings", {
        digest_hour: settings.digest_hour ?? 7, digest_size: settings.digest_size ?? 5,
        reading_goal: settings.reading_goal ?? 4, alert_email: Boolean(settings.alert_email),
        week_digest: settings.week_digest !== false, voice, speed, ...patch,
      });
    } catch (e) {
      console.error("could not keep that voice choice", e);
    }
  };

  // the whole morning feed, one story after another, for the commute
  const listenToEverything = () =>
    playStories((feed?.sections || []).flatMap((s) => s.rows)
      .slice(0, 12)
      .map((s) => ({ story_id: s.story_id, title: s.title, source: s.source, url: s.url })));

  const labelOf = (key) => cats.all.find((c) => c.key === key)?.label || key;

  // Drag a followed category to move it — the feed follows this order.
  const dropAt = async (index) => {
    if (dragging === null || dragging === index) return;
    const next = [...cats.mine];
    next.splice(index, 0, next.splice(dragging, 1)[0]);
    setDragging(null);
    setCats((c) => ({ ...c, mine: next }));
    try {
      await api.put("/news/categories", { categories: next });
      await loadFeed();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that order");
    }
  };

  const toggleCategory = async (key) => {
    const next = cats.mine.includes(key) ? cats.mine.filter((c) => c !== key) : [...cats.mine, key];
    try {
      const { data } = await api.put("/news/categories", { categories: next });
      setCats((c) => ({ ...c, mine: data.mine }));
      await loadFeed();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save those categories");
    }
  };

  const toggleFavourite = async (key) => {
    const loved = settings.favourites || [];
    const wanted = loved.includes(key) ? loved.filter((k) => k !== key) : [...loved, key];
    setSettings((old) => ({ ...old, favourites: wanted }));
    try {
      await api.put("/news/favourites", { categories: wanted });
      const { data } = await api.get("/news/digest", { params: { lang: said_in } });
      setDigest(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sendMorningEmail = async () => {
    try {
      const { data } = await api.post("/news/morning/email/test");
      toast.success(`Sent to ${data.to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that email");
    }
  };

  const saveSettings = async (patch) => {
    const next = { digest_hour: settings.digest_hour, digest_size: settings.digest_size,
                   morning_hour: settings.morning_hour ?? settings.digest_hour,
                   walk_voice: settings.walk_voice || voice,
                   morning_email: Boolean(settings.morning_email),
                   reading_goal: settings.reading_goal || 5,
                   alert_email: Boolean(settings.alert_email),
                   week_digest: settings.week_digest !== false, ...patch };
    setSettings((s) => ({ ...s, ...patch }));
    try {
      await api.put("/news/settings", next);
      const { data } = await api.get("/news/digest", { params: { lang: said_in } });
      setDigest(data);
      const fresh = await api.get("/news/streak");
      setStreak(fresh.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const unmute = async (source) => {
    try {
      const { data } = await api.post("/news/mute", { source });
      setSettings((s) => ({ ...s, muted_sources: data.muted_sources }));
      await loadFeed();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not unmute that publisher");
    }
  };

  const queueStory = (story) =>
    setLineUp((rows) => rows.some((r) => r.story_id === story.story_id)
      ? rows
      : [...rows, { story_id: story.story_id, title: story.title, source: story.source,
                    url: story.url }]);

  const isQueued = (story) => lineUp.some((r) => r.story_id === story.story_id);

  // the whole feed as one bulletin: each story told, then a word about what is coming next
  const playStories = async (stories, from = 0) => {
    if (!stories.length) return toast.info("Nothing to listen to yet");
    let words = bulletinWords[said_in];
    if (!words) {
      words = await api.get("/news/bulletin/lines", { params: { lang: said_in } })
        .then(({ data }) => data).catch(() => null);
      if (words) setBulletinWords((kept) => ({ ...kept, [said_in]: words }));
    }
    setLineUp(stories);
    setSpot(null);
    speaker.playQueue(bulletin(stories, said_in, words).slice(from), forgetSpot);
  };

  const keepSpot = async (stories, at) => {
    try {
      await api.put("/news/listen/spot", { stories, at });
    } catch (e) {
      console.error("could not remember where the listen stopped", e);
    }
  };

  const forgetSpot = async () => {
    setSpot(null);
    try {
      await api.delete("/news/listen/spot");
    } catch (e) {
      console.error("could not forget the listen spot", e);
    }
  };


  // every headline in the reader's own language, to sit beside the original
  useEffect(() => {
    if (!bothLangs || !feed?.sections?.length) return;
    const rows = feed.sections.flatMap((s) => s.rows)
      .slice(0, 30)
      .map((s) => ({ story_id: s.story_id, title: s.title }));
    api.post("/news/titles", { lang: said_in, rows })
      .then(({ data }) => setAlsoTitles((kept) => ({ ...kept, ...data.titles })))
      .catch((e) => console.error("could not put the headlines in your language", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bothLangs, feed, said_in]);

  // how each country I follow is telling the same story
  const askCompare = async (story) => {
    setComparing(true);
    setCompare(null);
    try {
      const { data } = await api.get("/news/compare",
                                    { params: { q: story.title, lang: said_in }, timeout: 60000 });
      setCompare(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not gather the coverage");
    } finally {
      setComparing(false);
    }
  };

  const isPinned = (source) =>
    pinned.some((p) => p.toLowerCase() === String(source || "").toLowerCase());

  // a paper I trust always leads my feed
  const togglePin = async (source, on) => {
    try {
      const { data } = await api.put("/news/pins", { source, on });
      setPinned(data.pinned);
      toast.success(on ? `${source} will lead your feed` : `${source} unpinned`);
      await loadFeed(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not pin that publisher");
    }
  };

  const onMuted = async (muted_sources) => {
    setSettings((s) => ({ ...s, muted_sources }));
    await loadFeed();
  };

  const followWord = async (keyword) => {
    const wanted = (keyword || word).trim();
    if (!wanted) return;
    try {
      const { data } = await api.post("/news/keywords", { keyword: wanted });
      setSettings((s) => ({ ...s, keywords: data.keywords }));
      setWord("");
      await loadFeed();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not follow that");
    }
  };

  const hearMyWeek = async () => {
    await speaker.play("roundup", async () =>
      (await api.get("/news/roundup/say", { params: { lang: said_in } })).data.text);
    api.get("/news/streak").then(({ data }) => setStreak(data)).catch(() => {});
  };

  const clearAlerts = async () => {
    try {
      await api.post("/news/alerts/seen");
      setAlerts([]);
    } catch (e) {
      console.error("could not clear alerts", e);
    }
  };

  const loadTrending = async (near) => {
    setNearOnly(near);
    try {
      const { data } = await api.get("/news/trending", { params: { scope: near ? "near" : "all" } });
      setTrending(data.rows);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load trending stories");
    }
  };

  const runSearch = async () => {
    if (!q.trim() && !scope) return;
    setSearching(true);
    try {
      const { data } = await api.get("/news/search",
        { params: { q, category: scope, lang: said_in, days } });
      setResults(data.rows);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Nothing came back");
    } finally {
      setSearching(false);
    }
  };

  const lovedLabels = new Set((settings.favourites || []).map(labelOf));

  const dropWithUndo = async (section) => {
    if (section.key.startsWith("kw:")) {
      const word = section.label;
      await followWord(word);
      toast.success(`No longer following “${word}”`, {
        action: { label: "Undo", onClick: () => followWord(word) },
      });
      return;
    }
    const before = cats.mine;   // put the exact list back, not a second toggle
    await toggleCategory(section.key);
    toast.success(`Unfollowed ${section.label}`, {
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const { data } = await api.put("/news/categories", { categories: before });
            setCats((c) => ({ ...c, mine: data.mine }));
            await loadFeed();
          } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not put that back");
          }
        },
      },
    });
  };

  // where you are and your own country are not things to unfollow
  const tunable = (section) =>
    !["local", "national"].includes(section.key) && !section.key.startsWith("country:");

  // what pulled this stretch of the feed in
  const whySection = (section) => {
    if (section.key === "local") return "where you are";
    if (section.key === "national") return "your country";
    if (section.key.startsWith("kw:")) return `a word you follow`;
    if (section.key.startsWith("country:")) return "a country you follow";
    return (settings.favourites || []).includes(section.key)
      ? "a favourite topic — two in your digest"
      : "a topic you follow";
  };

  const openPreview = async () => {
    const wanted = !dailyPreview;
    setDailyPreview(wanted);
    if (!wanted) return;
    try {
      const { data } = await api.get("/news/digest/next", { params: { lang: said_in } });
      setNextUp(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not look ahead");
    }
  };

  const hearThePreview = () =>
    speaker.play("preview", async () =>
      (await api.get("/news/digest/say", { params: { lang: said_in } })).data.text);

  const readItOut = () =>
    speaker.play("news", async () =>
      (await api.get("/home/news/say", { params: { lang: said_in } })).data.text);

  const seenDigest = async () => {
    try {
      const { data } = await api.post("/news/digest/seen");
      setDigest((d) => ({ ...d, unseen: false }));
      if (data.streak) setStreak(data.streak);
    } catch (e) {
      console.error("could not mark the digest as seen", e);
    }
  };

  const dropSaved = async (story) => {
    try {
      await api.delete(`/news/saved/${story.story_id}`);
      setSaved((rows) => rows.filter((r) => r.story_id !== story.story_id));
      loadSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that");
    }
  };

  return (
    <div className="cml-theme mx-auto max-w-5xl px-5 py-12 sm:px-8" style={{ "--theme": accent }}
         data-testid="news-page">
      <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand">
        <Newspaper className="h-3.5 w-3.5" /> Newsroom
      </p>
      <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
        Your News Forum, Near and Far…
      </h1>
      <p className="mt-3 max-w-2xl text-base text-ink-soft">
        Pick the countries your news comes from, follow the topics and keywords you care about, and pin the
        papers you trust so they lead. Every headline gets a line of explanation, sits beside its translation,
        and MiLu will tell you the whole story out loud — one story or your entire morning as a single
        bulletin, in your voice, at your pace, in any of the 50 languages. Save what matters, compare how two
        countries cover the same story, and pick your listen back up wherever you stopped.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button
          data-testid="news-read-aloud"
          onClick={readItOut}
          disabled={Boolean(speaker.loading)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50 sm:rounded-full"
        >
          {speaker.loading === "news" ? <Loader2 className="h-4 w-4 animate-spin" />
            : speaker.playing === "news" ? <Square className="h-4 w-4" /> : <Radio className="h-4 w-4" />}
          {speaker.playing === "news" ? "Stop" : "Read me the news"}
        </button>
        <button
          data-testid="news-listen-everything"
          onClick={listenToEverything}
          disabled={loading || !feed?.sections?.length}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-brand/40 px-4 text-sm font-semibold text-brand transition-colors hover:bg-brand/10 active:scale-95 disabled:opacity-50 sm:min-h-[38px] sm:rounded-full sm:text-xs"
        >
          <ListMusic className="h-4 w-4" /> Listen to everything
        </button>
        <button
          data-testid="news-refresh"
          onClick={() => loadFeed(true)}
          disabled={loading}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-brand/40 px-4 text-sm font-semibold text-brand transition-colors hover:bg-brand/10 active:scale-95 disabled:opacity-50 sm:min-h-[38px] sm:rounded-full sm:text-xs"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {streak && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-panel px-3 py-1.5 text-xs"
           data-testid="news-streak">
          <Flame className={`h-3.5 w-3.5 ${streak.today ? "text-amber-300" : "text-ink-soft"}`} />
          {streak.days > 0
            ? <span><span className="font-semibold">{streak.days} day{streak.days === 1 ? "" : "s"}</span> of keeping up{streak.best > streak.days ? ` · best ${streak.best}` : ""}</span>
            : <span className="text-ink-soft">Read or listen today to start a streak</span>}
          <span className="text-ink-soft" data-testid="reading-goal-progress">
            · this week {streak.this_week}/{streak.goal}{streak.goal_met ? " — done" : ""}
          </span>
        </p>
      )}

      <ListenStreak onListen={readItOut} busy={Boolean(speaker.loading)} refreshKey={heard} />

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="news-read-language">
        <CountryPicker onChange={() => loadFeed(true)} />
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">Read it in</span>
        <LanguageSelect languages={languages} value={said_in} onChange={pickLanguage}
                        testId="news-language-select" compact />
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">Voice</span>
        {READ_VOICES.map((v) => (
          <button
            key={v.id}
            data-testid={`news-voice-${v.id}`}
            onClick={() => pickVoice(v.id)}
            className={`min-h-[30px] rounded-full border px-3 text-xs transition-colors ${
              voice === v.id ? "border-brand bg-brand/15 font-semibold text-brand"
                             : "border-white/10 text-ink-soft hover:border-brand/40"
            }`}
          >
            {v.label.split(" · ")[0]}
          </button>
        ))}
        <VoicePreview voice={voice} testid="news-voice-preview" />
        <button
          data-testid="news-both-langs"
          onClick={() => {
            const on = !bothLangs;
            setBothLangs(on);
            localStorage.setItem("chatmilu.news.bothlangs", on ? "1" : "0");
          }}
          title="Show each headline in its own language and in yours"
          className={`min-h-[30px] rounded-full border px-3 text-xs transition-colors ${
            bothLangs ? "border-brand bg-brand/15 font-semibold text-brand"
                      : "border-white/10 text-ink-soft hover:border-brand/40"
          }`}
        >
          Both languages
        </button>
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">Pace</span>
        {[["0.85", "Slower"], ["1", "Normal"], ["1.2", "Brisk"], ["1.4", "Quick"]].map(([rate, label]) => (
          <button
            key={rate}
            data-testid={`news-speed-${rate}`}
            onClick={() => pickSpeed(Number(rate))}
            className={`min-h-[30px] rounded-full border px-3 text-xs transition-colors ${
              speed === Number(rate) ? "border-brand bg-brand/15 font-semibold text-brand"
                                     : "border-white/10 text-ink-soft hover:border-brand/40"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="mt-6 rounded-3xl border border-brand/40 bg-brand/10 p-5" data-testid="news-alerts">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <Zap className="h-4 w-4 text-brand" /> Your Personal Preference
            <span className="ms-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-page">
              {alerts.length}
            </span>
            <BoxPlayers rows={alerts.slice(0, alertsShown)} speaker={speaker} lang={said_in}
                        name="alerts" />
          </p>
          <ul className="mt-2 space-y-1.5">
            {alerts.slice(0, alertsShown).map((a) => (
              <li key={a.alert_id} className="text-sm" data-testid={`alert-${a.alert_id}`}>
                <a href={a.url} target="_blank" rel="noreferrer" className="hover:text-brand" dir="auto">
                  {a.title}
                </a>
                <span className="ms-2 inline-flex items-center gap-1.5 text-xs text-ink-soft">
                  <span className="rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-[10px] text-brand"
                        data-testid={`alert-why-${a.alert_id}`}
                        title={`You follow "${a.keyword}"`}>
                    following {a.keyword}
                  </span>
                  {a.source}
                </span>
                <ListenLine story={a} speaker={speaker} lang={said_in}
                            onQueue={queueStory} queued={isQueued(a)}
                            testid={`alert-listen-${a.alert_id}`} />
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center gap-3">
            <button
              data-testid="alerts-seen"
              onClick={clearAlerts}
              className="inline-flex min-h-[34px] items-center rounded-full border border-brand/50 px-4 text-xs font-semibold text-brand transition-colors hover:bg-brand/10"
            >
              Got it
            </button>

            {alerts.length > alertsShown && (
              <button
                data-testid="alerts-more"
                onClick={() => setAlertsShown((n) => n + 5)}
                aria-label="Show five more stories"
                title={`${alerts.length - alertsShown} more`}
                className="ms-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-brand/50 text-brand transition-transform hover:bg-brand/10 active:scale-90"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {digest?.picks?.length > 0 && (
        <div className="mt-6 rounded-3xl border border-brand/40 bg-brand/10 p-5" data-testid="news-digest">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <Bell className="h-4 w-4 text-brand" /> Your Daily Digest
            {digest.unseen && (
              <span className="ms-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-white">NEW</span>
            )}
            <BoxPlayers rows={digest.picks.slice(0, digestShown)} speaker={speaker} lang={said_in}
                        name="digest" />
          </p>
          <ul className="mt-2 space-y-1.5">
            {digest.picks.slice(0, digestShown).map((p) => (
              <li key={p.story_id} className="text-sm">
                <a href={p.url} target="_blank" rel="noreferrer" className="hover:text-brand" dir="auto">
                  {p.title}
                </a>
                <span className="ms-2 inline-flex items-center gap-1 text-xs text-ink-soft">
                  {lovedLabels.has(p.label) && (
                    <Star className="h-2.5 w-2.5 fill-current text-brand"
                          data-testid={`digest-favourite-${p.story_id}`}
                          title="One of your favourite topics" />
                  )}
                  {p.label} · {p.source}
                </span>
                <ListenLine story={p} speaker={speaker} lang={said_in}
                            onQueue={queueStory} queued={isQueued(p)}
                            testid={`digest-listen-${p.story_id}`} />
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center gap-3">
            {digest.unseen ? (
            <button
              data-testid="news-digest-seen"
              onClick={seenDigest}
              className="inline-flex min-h-[34px] items-center rounded-full border border-brand/40 px-4 text-xs font-semibold text-brand transition-colors hover:bg-brand/10"
            >
              Got it
            </button>
            ) : (
              <span className="text-[11px] text-ink-soft" data-testid="news-digest-read">
                Read today — play it again whenever you like
              </span>
            )}

            {digest.picks.length > digestShown && (
              <button
                data-testid="digest-more"
                onClick={() => setDigestShown((n) => n + 5)}
                aria-label="Show five more stories"
                title={`${digest.picks.length - digestShown} more`}
                className="ms-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-brand/50 text-brand transition-transform hover:bg-brand/10 active:scale-90"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {dailyPreview && (
        <div className="mt-6 rounded-3xl border border-white/10 bg-panel p-5" data-testid="daily-digest-preview">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <Bell className="h-4 w-4 text-brand" /> Tomorrow's digest
            <span className="text-xs font-normal text-ink-soft">
              {nextUp
                ? `${nextUp.planned} stories, `
                  + `${new Date(`${nextUp.day}T12:00:00Z`).toLocaleDateString(undefined,
                      { weekday: "long", timeZone: "UTC" })} `
                  + `${String(nextUp.hour).padStart(2, "0")}:00 (${nextUp.timezone})`
                : "looking ahead…"}
            </span>
          </p>

          {nextUp?.shape?.length > 0 && (
            <p className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="next-digest-shape">
              {nextUp.shape.map((row) => (
                <span key={row.label}
                      data-testid={`next-shape-${row.kind}`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                        row.favourite && nextUp.favourites.length
                          ? "border-brand/50 bg-brand/10 text-brand" : "border-white/12 text-ink-soft"}`}>
                  {row.label} × {row.stories}
                  {row.favourite && nextUp.favourites.length ? <Star className="h-2.5 w-2.5 fill-current" /> : null}
                </span>
              ))}
            </p>
          )}

          <p className="mt-3 text-[11px] uppercase tracking-widest text-ink-soft">
            Today's stories, as a taste of the mix
          </p>
          <button
            data-testid="daily-digest-hear"
            onClick={hearThePreview}
            disabled={Boolean(speaker.loading) || !digest?.picks?.length}
            className="mt-3 inline-flex min-h-[34px] items-center gap-2 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
          >
            {speaker.loading === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : speaker.playing === "preview" ? <Square className="h-3.5 w-3.5" />
              : <Radio className="h-3.5 w-3.5" />}
            {speaker.playing === "preview" ? "Stop" : "Hear it before it lands"}
          </button>

          {digest?.picks?.length ? (
            <ul className="mt-3 space-y-1.5">
              {digest.picks.map((p, i) => (
                <li key={`${p.story_id}-${i}`} className="text-sm" data-testid={`preview-pick-${i}`}>
                  <span className="me-2 inline-flex items-center gap-1 rounded-full border border-white/12 px-2 py-0.5 text-[10px] text-ink-soft">
                    {p.label}
                    {lovedLabels.has(p.label) && <Star className="h-2.5 w-2.5 fill-current text-brand" />}
                  </span>
                  <a href={p.url} target="_blank" rel="noreferrer" className="hover:text-brand" dir="auto">
                    {p.title}
                  </a>
                  <span className="ms-2 text-xs text-ink-soft">{p.source}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-ink-soft" data-testid="daily-digest-preview-empty">
              Nothing to show yet — follow a topic or two and it fills up.
            </p>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="news-digest-settings">
        <button
          data-testid="daily-digest-preview-toggle"
          onClick={openPreview}
          className="inline-flex min-h-[34px] items-center gap-2 rounded-full border border-brand/50 px-4 text-xs font-semibold text-brand transition-colors hover:bg-brand/10"
        >
          <Bell className="h-3.5 w-3.5" /> {dailyPreview ? "Hide the preview" : "Preview what lands next"}
        </button>
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">Digest lands at</span>
        <select
          data-testid="digest-hour"
          value={settings.digest_hour}
          onChange={(e) => saveSettings({ digest_hour: Number(e.target.value) })}
          className="min-h-[34px] rounded-full border border-white/15 bg-panel px-3 text-xs"
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
          ))}
        </select>
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">with</span>
        <select
          data-testid="digest-size"
          value={settings.digest_size}
          onChange={(e) => saveSettings({ digest_size: Number(e.target.value) })}
          className="min-h-[34px] rounded-full border border-white/15 bg-panel px-3 text-xs"
        >
          {[3, 4, 5, 6, 8, 10].map((n) => (
            <option key={n} value={n}>{n} stories</option>
          ))}
        </select>
        <span className="text-xs text-ink-soft">
          {settings.timezone ? `your time (${settings.timezone})` : "UTC — set a home timezone in the calendar"}
        </span>
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">MiLu greets me at</span>
        <select
          data-testid="morning-hour"
          value={settings.morning_hour ?? settings.digest_hour}
          onChange={(e) => saveSettings({ morning_hour: Number(e.target.value) })}
          className="min-h-[34px] rounded-full border border-white/15 bg-panel px-3 text-xs"
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
          ))}
        </select>
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">Weekends</span>
        <select
          data-testid="weekend-hour"
          value={settings.weekend_hour ?? ""}
          onChange={(e) => saveSettings({
            weekend_hour: e.target.value === "" ? -1 : Number(e.target.value) })}
          className="min-h-[34px] rounded-full border border-white/15 bg-panel px-3 text-xs"
        >
          <option value="">same as weekdays</option>
          {[7, 8, 9, 10, 11, 12].map((h) => (
            <option key={h} value={h}>from {String(h).padStart(2, "0")}:00</option>
          ))}
        </select>
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">Walks voice</span>
        <select
          data-testid="walk-voice"
          value={settings.walk_voice || voice}
          onChange={(e) => saveSettings({ walk_voice: e.target.value })}
          className="min-h-[34px] rounded-full border border-white/15 bg-panel px-3 text-xs"
        >
          {READ_VOICES.map((v) => (
            <option key={v.id} value={v.id}>{v.label.split(" · ")[0]}</option>
          ))}
        </select>
        <button
          data-testid="morning-email-toggle"
          onClick={() => saveSettings({ morning_email: !settings.morning_email })}
          className={`min-h-[34px] rounded-full border px-3.5 text-xs transition-colors ${
            settings.morning_email ? "border-brand bg-brand/15 font-semibold text-brand"
                                   : "border-white/10 text-ink-soft hover:border-brand/40"}`}
        >
          Email me my morning choices
        </button>
        <button
          data-testid="morning-email-now"
          onClick={sendMorningEmail}
          className="min-h-[34px] rounded-full border border-brand/50 px-3.5 text-xs text-brand transition-colors hover:bg-brand/10"
        >
          Send one now
        </button>
        <MorningChoice folders={folders.folders} />
        <span className="ms-2 text-[11px] uppercase tracking-widest text-ink-soft">Weekly goal</span>
        <select
          data-testid="reading-goal"
          value={settings.reading_goal || 5}
          onChange={(e) => saveSettings({ reading_goal: Number(e.target.value) })}
          className="min-h-[34px] rounded-full border border-white/15 bg-panel px-3 text-xs"
        >
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <option key={n} value={n}>{n} day{n === 1 ? "" : "s"} a week</option>
          ))}
        </select>
        <button
          data-testid="alert-email-toggle"
          onClick={() => saveSettings({ alert_email: !settings.alert_email })}
          className={`min-h-[34px] rounded-full border px-3.5 text-xs transition-colors ${
            settings.alert_email ? "border-brand bg-brand/15 font-semibold text-brand"
                                 : "border-white/10 text-ink-soft hover:border-brand/40"}`}
        >
          Email me keyword alerts
        </button>
        <button
          data-testid="week-digest-toggle"
          onClick={() => saveSettings({ week_digest: settings.week_digest === false })}
          className={`min-h-[34px] rounded-full border px-3.5 text-xs transition-colors ${
            settings.week_digest !== false ? "border-brand bg-brand/15 font-semibold text-brand"
                                           : "border-white/10 text-ink-soft hover:border-brand/40"}`}
        >
          Monday note about my week
        </button>
        <DigestPreview />
      </div>

      {pinned.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="news-pinned">
          <span className="text-[11px] uppercase tracking-widest text-ink-soft">Leading my feed</span>
          {pinned.map((src) => (
            <button
              key={src}
              data-testid={`unpin-${src}`}
              onClick={() => togglePin(src, false)}
              title="Tap to unpin"
              className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-brand bg-brand/15 px-3 text-xs font-semibold text-brand"
            >
              {src} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <PinSuggestions onPin={togglePin} refreshKey={pinned.length} />
      <FolderCommentAlerts />

      {settings.muted_sources?.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="news-muted">
          <span className="text-[11px] uppercase tracking-widest text-ink-soft">Muted</span>
          {settings.muted_sources.map((src) => (
            <button
              key={src}
              data-testid={`unmute-${src}`}
              onClick={() => unmute(src)}
              title="Tap to hear from them again"
              className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft hover:border-brand/40 hover:text-ink"
            >
              {src} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2" data-testid="news-categories">
        <span className="self-center text-[11px] uppercase tracking-widest text-ink-soft">I follow</span>
        {cats.mine.map((key, i) => (
          <span
            key={key}
            draggable
            onDragStart={() => setDragging(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => dropAt(i)}
            className="inline-flex min-h-[34px] cursor-grab items-center gap-1 rounded-full border border-brand bg-brand/15 ps-3.5 pe-1.5 text-xs font-semibold text-brand active:cursor-grabbing"
          >
            <button
              data-testid={`news-category-${key}`}
              onClick={() => toggleCategory(key)}
              title="Drag to reorder, tap to unfollow"
            >
              {labelOf(key)}
            </button>
            <button
              data-testid={`news-favourite-${key}`}
              onClick={() => toggleFavourite(key)}
              title={(settings.favourites || []).includes(key)
                ? "A favourite — two stories in your digest"
                : "Make it a favourite: two stories in your digest"}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-brand/20"
            >
              <Star className={`h-3 w-3 ${(settings.favourites || []).includes(key)
                ? "fill-current" : "opacity-40"}`} />
            </button>
          </span>
        ))}
        <span className="mx-1 h-4 w-px bg-white/10" />
        {cats.all.filter((c) => !cats.mine.includes(c.key)).map((c) => (
          <button
            key={c.key}
            data-testid={`news-category-${c.key}`}
            onClick={() => toggleCategory(c.key)}
            className="min-h-[34px] rounded-full border border-white/10 px-3.5 text-xs text-ink-soft transition-colors hover:border-brand/40"
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="news-keywords">
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">My Personal Preferences</span>
        {(settings.keywords || []).map((k) => (
          <button
            key={k}
            data-testid={`unfollow-${k}`}
            onClick={() => followWord(k)}
            title="Tap to stop following"
            className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-brand bg-brand/15 px-3 text-xs font-semibold text-brand"
          >
            {k} <X className="h-3 w-3" />
          </button>
        ))}
        <input
          data-testid="keyword-input"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && followWord()}
          placeholder="A club, a company, a person…"
          maxLength={40}
          className="min-h-[30px] w-52 rounded-full border border-white/15 bg-panel px-3 text-xs outline-none focus:border-brand/50"
        />
        <button
          data-testid="keyword-add"
          onClick={() => followWord()}
          disabled={!word.trim()}
          className="inline-flex min-h-[30px] items-center gap-1 rounded-full bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
        >
          <Plus className="h-3 w-3" /> Follow
        </button>
      </div>

      <button
        data-testid="coffee-break-toggle"
        onClick={toggleQuick}
        title="Only stories measured at five minutes or less"
        className={`mt-3 inline-flex min-h-[32px] items-center gap-1.5 rounded-full border px-3 text-xs transition-colors ${
          quickOnly ? "border-brand bg-brand/15 font-semibold text-brand"
                    : "border-white/10 text-ink-soft hover:border-brand/40 hover:text-ink"}`}
      >
        <Coffee className="h-3.5 w-3.5" />
        Coffee break · under 5 min
      </button>

      <button
        data-testid="walk-playlist-button"
        onClick={playWalk}
        disabled={Boolean(speaker.loading)}
        title="One continuous listen of every short story"
        className="ms-2 mt-3 inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
      >
        {speaker.loading === "walk" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : speaker.playing === "walk" ? <Square className="h-3.5 w-3.5" />
          : <Footprints className="h-3.5 w-3.5" />}
        {speaker.playing === "walk" ? "Stop the walk" : `Listen while walking · ${walkLength} min`}
      </button>
      <span className="ms-2 inline-flex overflow-hidden rounded-full border border-white/10 align-middle"
            data-testid="walk-length">
        {[5, 10, 20].map((mins) => (
          <button
            key={mins}
            data-testid={`walk-length-${mins}`}
            onClick={() => pickWalkLength(mins)}
            className={`min-h-[32px] px-3 text-xs transition-colors ${
              walkLength === mins ? "bg-brand/15 font-semibold text-brand"
                                  : "text-ink-soft hover:text-ink"}`}
          >
            {mins} min
          </button>
        ))}
      </span>
      <button
        data-testid="refresh-all-categories"
        onClick={refreshAll}
        title="Three fresh stories in every category"
        className="ms-2 mt-3 inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
      >
        <RefreshCw className="h-3.5 w-3.5" /> Refresh all categories
      </button>
      {walk && speaker.playing === "walk" && (
        <p className="mt-2 text-xs text-ink-soft" data-testid="walk-summary">
          {walk.stories} short stories · about {walk.minutes} minutes of reading in one run
        </p>
      )}
      <WalkHistory speaker={speaker} refreshKey={walksKey} />

      <div className="mt-6 flex gap-2 border-b border-white/10" data-testid="news-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`news-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`inline-flex min-h-[40px] items-center gap-2 border-b-2 px-3 text-sm transition-colors ${
              tab === t.key ? "border-brand font-semibold text-brand"
                            : "border-transparent text-ink-soft hover:text-ink"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
            {t.key === "saved" && (folders.total || saved.length) > 0 && (
              <span className="rounded-full bg-white/10 px-1.5 text-[10px]">{folders.total || saved.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "feed" && spot && !speaker.playing && (
        <ResumeListen spot={spot} onDismiss={forgetSpot}
                      onResume={() => playStories(spot.stories, spot.at)} />
      )}

      <ListenQueue rows={lineUp} speaker={speaker} lang={said_in} onPlayAll={() => playStories(lineUp)}
                   alsoTitles={bothLangs ? alsoTitles : {}}
                   onRemove={(s) => setLineUp((r) => r.filter((x) => x.story_id !== s.story_id))}
                   onClear={() => { setLineUp([]); forgetSpot(); }} />
      <ReadAlong speaker={speaker} />

      {tab === "feed" && (
        <ListenHistory speaker={speaker} lang={said_in} refreshKey={heard}
                       onQueue={queueStory} isQueued={isQueued} />
      )}

      {tab === "feed" && <BlockedPublishers onMuted={onMuted} refreshKey={heard} />}

      {(compare || comparing) && (
        <CompareCoverage data={compare} loading={comparing} lang={said_in} speaker={speaker}
                         onClose={() => setCompare(null)} />
      )}

      {tab === "feed" && (feed?.countries?.length || 0) > 1 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="flag-filter">
          <button
            data-testid="flag-filter-all"
            onClick={() => setOnly("")}
            className={`min-h-[30px] rounded-full border px-3 text-xs transition-colors ${
              only ? "border-white/10 text-ink-soft hover:border-brand/40"
                   : "border-brand bg-brand/15 font-semibold text-brand"}`}
          >
            All countries
          </button>
          {feed.countries.map((name) => {
            const flag = (feed.sections.find((s) => s.label === name) || {}).flag || "";
            return (
              <button
                key={name}
                data-testid={`flag-filter-${flag}`}
                onClick={() => setOnly((f) => (f === flag ? "" : flag))}
                className={`inline-flex min-h-[30px] items-center gap-2 rounded-full border px-3 text-xs transition-colors ${
                  only === flag ? "border-brand bg-brand/15 font-semibold text-brand"
                                : "border-white/10 text-ink-soft hover:border-brand/40"}`}
              >
                <Flag country={flag} className="h-[13px] w-[18px]" /> {name}
              </button>
            );
          })}
        </div>
      )}

      {tab === "feed" && (
        <div className="mt-6 space-y-8" data-testid="news-feed">
          {sundayEvening && roundup && (
            <button
              data-testid="sunday-roundup"
              onClick={hearMyWeek}
              className="flex w-full items-center gap-3 rounded-2xl border border-brand/40 bg-brand/10 px-4 py-3 text-start text-sm transition-colors hover:bg-brand/15"
            >
              <CalendarRange className="h-4 w-4 shrink-0 text-brand" />
              <span className="min-w-0 flex-1">
                It's Sunday evening — shall MiLu read you your week in news?
              </span>
              <span className="shrink-0 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">
                {speaker.playing === "roundup" ? "Stop" : "Hear it"}
              </span>
            </button>
          )}
          {loading && !feed && <p className="text-sm text-ink-soft">Fetching the headlines…</p>}
          {waiting > 0 && (
            <button
              data-testid="new-stories-pill"
              onClick={() => { setWaiting(0); loadFeed(true); }}
              className="mx-auto flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-brand/20"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {waiting} new {waiting === 1 ? "story" : "stories"} · tap to load
            </button>
          )}

          {(feed?.sections || []).filter((s) => !only || s.flag === only).map((section) => {
            const pool = coffeeBreak(section.rows);
            const start = ((pages[section.key] || 0) * 3) % Math.max(pool.length, 1);
            const three = [...pool.slice(start), ...pool.slice(0, start)].slice(0, 3);
            return (
              <section key={section.key} data-testid={`news-section-${section.key}`}>
                <div className="flex items-center gap-3">
                  <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg">
                    {section.flag && (
                      <Flag country={section.flag} className="h-[16px] w-[22px]"
                            testId={`section-flag-${section.key}`} />
                    )}
                    {section.label}
                  </h2>
                  {tunable(section) ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`section-why-${section.key}`}
                          title="Tap to favourite or unfollow"
                          className="shrink-0 rounded-full border border-white/12 px-2 py-0.5 text-[10px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                        >
                          {whySection(section)}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-[220px]">
                        {section.key.startsWith("kw:") ? (
                          <DropdownMenuItem
                            data-testid={`why-unfollow-${section.key}`}
                            onClick={() => dropWithUndo(section)}
                          >
                            Stop following “{section.label}”
                          </DropdownMenuItem>
                        ) : (
                          <>
                            <DropdownMenuItem
                              data-testid={`why-favourite-${section.key}`}
                              onClick={() => toggleFavourite(section.key)}
                            >
                              {(settings.favourites || []).includes(section.key)
                                ? "No longer a favourite"
                                : "Make it a favourite — two in my digest"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`why-unfollow-${section.key}`}
                              onClick={() => dropWithUndo(section)}
                            >
                              Unfollow {section.label}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="shrink-0 rounded-full border border-white/12 px-2 py-0.5 text-[10px] text-ink-soft"
                          data-testid={`section-why-${section.key}`}>
                      {whySection(section)}
                    </span>
                  )}
                  {pool.length > 3 && (
                    <button
                      data-testid={`section-refresh-${section.key}`}
                      onClick={() => nextThree(section.key)}
                      title={`Show three more from ${section.label}`}
                      className="ms-auto inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                  )}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {three.map((story) => (
                    <StoryCard key={story.story_id} story={story} speaker={speaker} onMuted={onMuted}
                               onSavedChange={loadSaved} label={section.label}
                               pinned={isPinned(story.source)} onPin={togglePin} lang={said_in}
                               onQueue={queueStory} queued={isQueued(story)}
                               alsoTitle={bothLangs ? alsoTitles[story.story_id] : ""}
                               onCompare={(feed?.countries?.length || 0) > 1 ? askCompare : null}
                               skin={tiles[tileKey(story.source)]} onSkin={pickTileSkin} />
                  ))}
                </div>
              </section>
            );
          })}
          {quickOnly && feed?.sections?.every((s) => coffeeBreak(s.rows).length === 0) && (
            <p className="text-sm text-ink-soft" data-testid="news-no-quick">
              Nothing short enough right now — turn off Coffee break to see everything.
            </p>
          )}
          {feed?.sections?.every((s) => s.rows.length === 0) && (
            <p className="text-sm text-ink-soft" data-testid="news-empty">
              No headlines came back just now. Try Refresh in a minute.
            </p>
          )}
        </div>
      )}

      {tab === "trending" && (
        <div className="mt-6" data-testid="news-trending">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-ink-soft">
              What members found most interesting in the last day and a half.
            </p>
            <div className="ms-auto flex gap-2">
              <button
                data-testid="trending-all"
                onClick={() => loadTrending(false)}
                className={`min-h-[32px] rounded-full border px-3 text-xs transition-colors ${
                  !nearOnly ? "border-brand bg-brand/15 font-semibold text-brand"
                            : "border-white/10 text-ink-soft hover:border-brand/40"}`}
              >
                Everyone
              </button>
              <button
                data-testid="trending-near"
                onClick={() => loadTrending(true)}
                className={`min-h-[32px] rounded-full border px-3 text-xs transition-colors ${
                  nearOnly ? "border-brand bg-brand/15 font-semibold text-brand"
                           : "border-white/10 text-ink-soft hover:border-brand/40"}`}
              >
                Near me
              </button>
            </div>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {coffeeBreak(trending).map((story) => (
              <StoryCard key={story.story_id} story={story} speaker={speaker} onMuted={onMuted}
                             onSavedChange={loadSaved} label="Trending" lang={said_in} />
            ))}
          </div>
          {trending?.length === 0 && (
            <p className="mt-4 text-sm text-ink-soft" data-testid="trending-empty">
              Nothing has been voted on yet — give a story a thumbs up and it'll show here.
            </p>
          )}
        </div>
      )}

      {tab === "search" && (
        <div className="mt-6" data-testid="news-search">
          <div className="flex flex-wrap gap-2">
            <input
              data-testid="news-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Keywords — a person, a place, a thing…"
              className="min-h-[44px] min-w-0 flex-1 rounded-2xl border border-white/15 bg-panel px-4 text-sm outline-none focus:border-brand/50"
            />
            <select
              data-testid="news-search-category"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="min-h-[44px] rounded-2xl border border-white/15 bg-panel px-3 text-sm"
            >
              <option value="">Any category</option>
              {cats.all.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select
              data-testid="news-search-range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="min-h-[44px] rounded-2xl border border-white/15 bg-panel px-3 text-sm"
            >
              {RANGES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
            <button
              data-testid="news-search-go"
              onClick={runSearch}
              disabled={searching || (!q.trim() && !scope)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-search-results">
            {coffeeBreak(results).map((story) => (
              <StoryCard key={story.story_id} story={story} speaker={speaker} onMuted={onMuted}
                             onSavedChange={loadSaved} lang={said_in} />
            ))}
          </div>
          {results?.length === 0 && (
            <p className="mt-5 text-sm text-ink-soft" data-testid="news-search-empty">
              Nothing found for that. Try fewer words or a wider time range.
            </p>
          )}
        </div>
      )}

      {tab === "weekend" && <WeekendList lang={said_in} speaker={speaker} />}

      {tab === "saved" && (
        <div className="mt-6 space-y-3" data-testid="news-saved">
          <div className="flex flex-wrap items-center gap-2" data-testid="saved-read-language">
            <span className="text-[11px] uppercase tracking-widest text-ink-soft">Read my saved news in</span>
            <LanguageSelect languages={languages} value={savedLang || said_in} onChange={pickSavedLang}
                            testId="saved-language-select" compact />
            {savedBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />}
            {savedLang && (
              <button
                data-testid="saved-show-original"
                onClick={() => pickSavedLang("")}
                className="min-h-[30px] rounded-full border border-white/10 px-3 text-xs text-ink-soft transition-colors hover:border-brand/40 hover:text-ink"
              >
                Show originals
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="saved-search">
            <Search className="h-3.5 w-3.5 text-brand" />
            <input
              data-testid="saved-search-input"
              value={noteQuery}
              onChange={(e) => searchNotes(e.target.value)}
              maxLength={80}
              placeholder="Search my notes and headlines…"
              className="min-h-[34px] w-72 rounded-full border border-white/10 bg-page px-4 text-sm outline-none focus:border-brand/50"
            />
            {noteQuery && (
              <button data-testid="saved-search-clear" onClick={() => searchNotes("")}
                      className="text-xs text-ink-soft underline decoration-dotted hover:text-ink">
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="saved-folders">
            <span className="text-[11px] uppercase tracking-widest text-ink-soft">Folders</span>
            {[{ key: "", label: "All", n: folders.total },
              { key: "unsorted", label: "Unsorted", n: folders.counts?.[""] || 0 }].map((box) => (
              <button
                key={box.key || "all"}
                data-testid={`saved-folder-tab-${box.key || "all"}`}
                onClick={() => pickFolder(box.key)}
                className={`min-h-[30px] rounded-full border px-3 text-xs transition-colors ${
                  folder === box.key ? "border-brand bg-brand/15 font-semibold text-brand"
                                     : "border-white/10 text-ink-soft hover:border-brand/40"
                }`}
              >
                {box.label} {box.n > 0 && <span className="text-ink-soft">{box.n}</span>}
              </button>
            ))}
            {folders.folders.map((name) => (
              <span key={name}
                    className={`inline-flex min-h-[30px] items-center gap-1 rounded-full border px-3 text-xs transition-colors ${
                      folder === name ? "border-brand bg-brand/15 text-brand"
                                      : "border-white/10 text-ink-soft hover:border-brand/40"
                    }`}>
                <button data-testid={`saved-folder-tab-${name}`} onClick={() => pickFolder(name)}
                        className={folder === name ? "font-semibold" : ""}>
                  {name} {(folders.counts?.[name] || 0) > 0 && <span className="text-ink-soft">{folders.counts[name]}</span>}
                </button>
                <button data-testid={`saved-folder-drop-${name}`} onClick={() => dropFolder(name)}
                        aria-label={`Remove the ${name} folder`} className="text-ink-soft hover:text-ink">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              data-testid="saved-folder-input"
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addFolder()}
              maxLength={30}
              placeholder="Work, Football, Read Later…"
              className="min-h-[30px] w-44 rounded-full border border-white/10 bg-page px-3 text-xs outline-none focus:border-brand/50"
            />
            <button
              data-testid="saved-folder-add"
              onClick={addFolder}
              className="inline-flex min-h-[30px] items-center gap-1 rounded-full bg-brand/15 px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand/25"
            >
              <Plus className="h-3 w-3" /> Folder
            </button>
          </div>

          {folder && folder !== "unsorted" && (
            <div className="flex flex-wrap items-center gap-3" data-testid="saved-folder-actions">
              <button
                data-testid="saved-folder-share"
                onClick={() => (shares[folder] ? unshareFolder(folder) : shareFolder(folder))}
                className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-ink"
              >
                <Share2 className="h-3.5 w-3.5 text-brand" />
                {shares[folder] ? `Stop sharing “${folder}”` : `Share “${folder}” by link`}
              </button>
              {shares[folder] && (
                <button
                  data-testid="saved-folder-copy-link"
                  onClick={() => {
                    navigator.clipboard?.writeText(`${window.location.origin}/news/shared/${shares[folder]}`)
                      .catch(() => {});
                    toast.success("Link copied");
                  }}
                  className="text-xs text-brand underline decoration-dotted hover:text-brand-hover"
                >
                  Copy the link again
                </button>
              )}
              <button
                data-testid="saved-folder-preview"
                onClick={() => previewSunday(folder)}
                disabled={previewBusy}
                className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-ink disabled:opacity-50"
              >
                {previewBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                             : <MailPlus className="h-3.5 w-3.5 text-brand" />}
                Send me a preview
              </button>
              <label className="inline-flex min-h-[32px] cursor-pointer items-center gap-2 text-xs text-ink-soft">
                <input
                  type="checkbox"
                  data-testid="saved-folder-sunday"
                  checked={Boolean(sunday.on && sunday.folder === folder)}
                  onChange={() => toggleSunday(folder)}
                  className="h-3.5 w-3.5 accent-[var(--theme)]"
                />
                Email me “{folder}” on Sunday mornings
              </label>
            </div>
          )}

          {folder && folder !== "unsorted" && shares[folder] && (
            <FolderComments token={shares[folder]} canDelete />
          )}

          {preview && (
            <div className="rounded-2xl border border-white/10 bg-panel p-4" data-testid="digest-preview">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold" dir="auto">{preview.subject}</p>
                <span className="text-xs text-ink-soft">
                  {preview.sent ? `Sent to ${preview.to}` : "Not posted — preview only"}
                </span>
                <button data-testid="digest-preview-close" onClick={() => setPreview(null)}
                        className="ms-auto rounded-full p-1 text-ink-soft hover:text-ink">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <iframe
                title="Sunday email preview"
                data-testid="digest-preview-frame"
                srcDoc={preview.html}
                className="mt-3 h-[420px] w-full rounded-xl border border-white/10 bg-black"
              />
            </div>
          )}

          {saved.length > 0 && (
            <button
              data-testid="saved-digest-play"
              onClick={playSavedDigest}
              disabled={Boolean(speaker.loading)}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
            >
              {speaker.loading === "saved-digest" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : speaker.playing === "saved-digest" ? <Square className="h-3.5 w-3.5" />
                : <Volume2 className="h-3.5 w-3.5" />}
              {speaker.playing === "saved-digest"
                ? "Stop the playlist"
                : `Play ${folder && folder !== "unsorted" ? folder : "my saved"} as a playlist`}
            </button>
          )}
          {roundup && (

            <div className="rounded-3xl border border-white/10 bg-page p-5" data-testid="news-roundup">
              <p className="flex items-center gap-2 text-sm font-semibold">
                <CalendarRange className="h-4 w-4 text-brand" /> Your week in news
              </p>
              <p className="mt-1 text-sm text-ink-soft">
                Since {roundup.from}: {roundup.saved.length} saved, {roundup.liked.length} you found
                interesting, {roundup.comments} comment{roundup.comments === 1 ? "" : "s"}.
              </p>
              <button
                data-testid="roundup-listen"
                onClick={hearMyWeek}
                disabled={Boolean(speaker.loading)}
                className="mt-3 inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
              >
                {speaker.loading === "roundup" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : speaker.playing === "roundup" ? <Square className="h-3.5 w-3.5" />
                  : <Volume2 className="h-3.5 w-3.5" />}
                {speaker.playing === "roundup" ? "Stop" : "Hear my week"}
              </button>
              {roundup.liked.length > 0 && (
                <ul className="mt-2 space-y-1.5">
                  {roundup.liked.slice(0, 5).map((r) => (
                    <li key={r.story_id} className="text-sm">
                      <a href={r.url} target="_blank" rel="noreferrer" className="hover:text-brand" dir="auto">
                        {r.title}
                      </a>
                      <span className="ms-2 text-xs text-ink-soft">{r.source}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {saved.length === 0 && (
            <p className="text-sm text-ink-soft">
              {folder ? `Nothing in “${folder}” yet — move a saved story into it.`
                      : "Nothing saved yet — tap Save on a story you want to keep."}
            </p>
          )}
          {saved.map((story) => (
            <SavedStory
              key={story.story_id}
              story={story}
              speaker={speaker}
              folders={folders.folders}
              onChanged={() => loadSaved()}
              onRemove={dropSaved}
            />
          ))}
        </div>
      )}

      <PageGuide name="news" />

      <p className="mt-10 text-xs text-ink-soft">
        Headlines and links come from publishers via Google News. ChatMiLu shows the headline and always sends you
        to the publisher to read the article.
      </p>
    </div>
  );
}
